/*
 * NDK Farmacias — mapa, agrupación por provincia, lista y buscador.
 * Lee window.NDK_FARMACIAS (assets/ndk-farmacias-data.js) y window.google.maps
 * (Google Maps JS API, cargada solo si hay clave en los ajustes de la sección —
 * ver {% schema %} de sections/ndk-farmacias-section.liquid).
 * Expone window.NdkFarmacias = { mapa, marcadores, filtrar(texto), ordenarPorDistancia(lat,lng),
 *   diagnostico() }.
 * Incluye el cableado del buscador (texto + CP) y de "Usar mi ubicación" (Tarea 6).
 *
 * El mapa se arranca llamando a google.maps.importLibrary('maps') nosotros mismos (Tarea 7b),
 * no vía un callback= global invocado por el script de Google: así el arranque no depende de en
 * qué orden terminen de cargar la API de Google y este script. Sin clave configurada, la sección
 * no inserta el bootstrap loader de Google (ver sections/ndk-farmacias-section.liquid) y
 * google.maps.importLibrary no existe: la lista, el buscador y la geolocalización funcionan
 * exactamente igual, solo que window.NdkFarmacias.mapa se queda a null y no hay pines. La
 * sección ya muestra un aviso en el hueco del mapa en ese caso.
 */
(function () {
  'use strict';

  var CENTRO_ESPANA = { lat: 40.2, lng: -3.7 };
  var ZOOM_INICIAL = 5;
  var ZOOM_UMBRAL_CLUSTER = 8; // a partir de este zoom se muestran pines individuales en vez de agrupados
  var ZOOM_GRUPO_CON_NOMBRE = 6; // por debajo, burbuja con solo el número; a partir de aquí, píldora con el nombre
  var SEPARACION_GRUPOS_PX = 6; // aire mínimo entre dos etiquetas de grupo antes de fusionarlas

  // Huella aproximada de la etiqueta de un grupo, en píxeles de pantalla. Hace falta ANTES de
  // crear los overlays (que es cuando se decide qué grupos se fusionan), así que se estima en
  // vez de medirse en el DOM. Los números salen de medir las etiquetas reales en el preview:
  // la burbuja siempre es un círculo de 34px, y la píldora mide 46px de alto y unos 8,2px por
  // carácter del topónimo en mayúsculas —a 10px, peso 600 y 1px de tracking— más 24px de
  // padding y borde. El ancho lo manda el NOMBRE, no el número: por eso «Santa Cruz de
  // Tenerife» medía 170px y «Gijón» 60px.
  function huellaGrupo(nombre, conNombre) {
    if (!conNombre || !nombre) return { ancho: 34, alto: 34 };
    return { ancho: Math.max(34, 24 + String(nombre).length * 8.2), alto: 46 };
  }
  var ZOOM_MAXIMO_RESULTADO = 15; // tope al recolocar el mapa sobre resultados: evita que una
                                   // única farmacia (o dos muy próximas) dejen el mapa a zoom
                                   // de portal

  var elMapa = document.getElementById('ndk-farmacias-mapa');
  // Div interno dedicado solo a Google (Tarea 7b): new google.maps.Map() vacía por completo el
  // div que se le pasa, así que Google recibe este en vez de elMapa directamente — si no, se
  // llevaría por delante a la ficha inferior (#ndk-farmacias-ficha), hermana suya dentro de
  // elMapa. Ver el comentario en sections/ndk-farmacias-section.liquid junto a
  // #ndk-farmacias-mapa-google.
  var elMapaGoogle = document.getElementById('ndk-farmacias-mapa-google');
  var elLista = document.getElementById('ndk-farmacias-lista');

  // TODAS ya no es el array del Excel a secas: es el resultado de fusionar el Excel con los
  // bloques del Theme Editor. Se asigna más abajo, justo después de definir fusionar() — ver
  // la sección "fusión con los bloques del Theme Editor". Sin bloques, fusionar() devuelve el
  // MISMO array que venía en window.NDK_FARMACIAS, sin copiar ni tocar nada.
  var TODAS;
  var FUSION;

  // ---------- utilidades ----------

  function normalizarTexto(str) {
    return String(str == null ? '' : str)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  function capitalizarPalabras(str) {
    return String(str == null ? '' : str)
      .toLowerCase()
      .replace(/(^|[\s-])([a-záéíóúñü])/g, function (m, sep, letra) {
        return sep + letra.toUpperCase();
      });
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function tieneCoordenadas(f) {
    return typeof f.lat === 'number' && typeof f.lng === 'number';
  }

  function distanciaHaversine(lat1, lng1, lat2, lng2) {
    var R = 6371; // km
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLng = (lng2 - lng1) * Math.PI / 180;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function formatearDistancia(km) {
    if (km < 10) {
      return 'a ' + km.toFixed(1).replace('.', ',') + ' km';
    }
    return 'a ' + Math.round(km) + ' km';
  }

  function prefiereMovimientoReducido() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function puntosDe(lista) {
    return lista.filter(tieneCoordenadas).map(function (f) { return [f.lat, f.lng]; });
  }

  // Proyección Mercator directa: pasa una coordenada a píxeles del mundo a un zoom dado. Es la
  // misma fórmula que usa Google por dentro, pero como FUNCIÓN PURA, y esa es exactamente la
  // razón de escribirla aquí en vez de llamar a mapa.getProjection(): la proyección de Google
  // devuelve null mientras el mapa no haya pintado un frame (pestaña de fondo, iframe fuera de
  // pantalla, bfcache) — el fantasma que ya costó horas en este proyecto y que documenta
  // diagnostico() más abajo. Solo se usa para medir distancias RELATIVAS entre grupos, así que
  // basta con que la escala sea coherente; no hace falta el offset del centro del mapa.
  function proyectarPixel(lat, lng, zoom) {
    var escala = 256 * Math.pow(2, zoom);
    var seno = Math.sin(lat * Math.PI / 180);
    seno = Math.min(Math.max(seno, -0.9999), 0.9999);
    return {
      x: (lng + 180) / 360 * escala,
      y: (0.5 - Math.log((1 + seno) / (1 - seno)) / (4 * Math.PI)) * escala
    };
  }

  // ---------- fusión con los bloques del Theme Editor ----------
  // La página tiene DOS fuentes y el editor manda sobre el Excel:
  //   1. El Excel del equipo comercial -> tools/farmacias/build.py -> window.NDK_FARMACIAS.
  //   2. Los bloques "Añadir / Corregir / Ocultar una farmacia" de la sección, serializados a
  //      <script type="application/json" id="ndk-farmacias-bloques"> (ver el Liquid).
  // Se emparejan por identificador (el mismo slug del nombre que genera build.py).
  //
  // Este es el ÚNICO punto de fusión de todo el archivo, y devuelve registros con exactamente
  // la misma forma y los mismos tipos que produce build.py: las 14 claves, `null` donde no hay
  // dato (nunca undefined), lat/lng como Number, cp y telefono como cadenas de dígitos. Esa es
  // la razón de fusionar aquí y no en cada sitio que pinta: ninguna función de render, búsqueda,
  // agrupación o mapa necesita saber que esta capa existe.
  //
  // Regla de oro: SIN BLOQUES NO PASA NADA. fusionar() devuelve el array original por
  // referencia, sin copiarlo ni recorrerlo. El caso sin bloques es el que está en producción.

  var CLAVES_REGISTRO = ['id', 'nombre', 'direccion', 'cp', 'ciudad', 'provincia', 'comunidad',
    'telefono', 'web', 'instagram', 'lat', 'lng', 'precision', 'alta'];

  // Recuadro de España con margen generoso para no dejar fuera Canarias ni Ceuta y Melilla
  // (lat 27,0-44,5 / lng -19,0 a 5,0). A DIFERENCIA de antes, unas coordenadas fuera de este
  // recuadro SÍ se RECHAZAN (Tarea 11 #1): es el único error del fundador que llegaba a la
  // página pública sin que nada lo parase —y uno facilísimo de cometer, porque Google Maps da
  // las coordenadas en un orden y es tentador pegarlas al revés—, así que un simple aviso no
  // bastaba. Rechazar no descarta la farmacia entera: el resto de los campos del bloque se
  // siguen aplicando y las coordenadas se tratan como si no se hubieran tocado (ver
  // aplicarCampos más abajo), la misma regla que ya rige para una farmacia sin coordenadas.
  var ESPANA_LAT_MIN = 27.0, ESPANA_LAT_MAX = 44.5, ESPANA_LNG_MIN = -19.0, ESPANA_LNG_MAX = 5.0;

  function limpiar(v) {
    return v == null ? '' : String(v).replace(/\s+/g, ' ').trim();
  }

  // Espejo exacto de _slug() en tools/farmacias/normalize.py: NFKD, fuera los diacríticos,
  // minúsculas, todo lo que no sea [a-z0-9] a guion, y guiones de los extremos fuera. Es
  // idempotente sobre un identificador ya bien formado, así que se aplica SIEMPRE al campo del
  // bloque: da igual que el fundador pegue "farmacia-lh7" o "FARMACIA LH7", ambos casan.
  function slugificar(texto) {
    return String(texto == null ? '' : texto)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Los normalizadores devuelven { valor } o { error }, y opcionalmente { aviso }. Son más
  // estrictos que los de normalize.py a propósito: allí la entrada es una hoja auditada, aquí
  // la teclea una persona en un formulario.

  function normalizarCP(v) {
    var d = limpiar(v).replace(/\D/g, '');
    if (d.length === 4) return { valor: '0' + d };   // Alicante y Ceuta empiezan por cero
    if (d.length === 5) return { valor: d };
    return { error: 'el código postal debe tener 5 cifras' };
  }

  function normalizarTelefono(v) {
    // Mismo criterio que _telefono() en normalize.py: menos de 9 dígitos no es un número
    // marcable, es un defecto de entrada. Reducirlo a dígitos es además lo que hace seguro
    // meterlo en un href="tel:".
    var d = limpiar(v).replace(/\D/g, '');
    if (d.length >= 9) return { valor: d };
    return { error: 'el teléfono necesita al menos 9 cifras' };
  }

  var RE_INSTAGRAM_URL = /^https?:\/\/(?:www\.)?instagram\.com\/([^/?#]+)/i;
  var RE_INSTAGRAM_HANDLE = /^[A-Za-z0-9._]{1,30}$/;

  // Instagram NO se escapa: se RECONSTRUYE a partir de un usuario validado. Es seguridad, no
  // cosmética. botonesHTML() mete este valor dentro de un href, y escapeHtml('javascript:...')
  // no altera nada — las comillas quedan intactas, el atributo no se rompe y al pulsar se
  // ejecuta. Mientras el dato venía de la lista blanca del Excel daba igual; con entrada del
  // editor deja de dar igual. Construyendo la URL nosotros, el esquema https es imposible de
  // cambiar desde el formulario.
  function normalizarInstagram(v) {
    var t = limpiar(v).replace(/^@+/, '');
    var handle = null;
    if (t.indexOf('/') !== -1) {
      var m = RE_INSTAGRAM_URL.exec(t);
      if (m) handle = m[1];
    } else {
      handle = t;
    }
    if (!handle || !RE_INSTAGRAM_HANDLE.test(handle)) {
      return { error: 'Instagram tiene que ser el usuario (@lafarmacia) o la dirección completa del perfil' };
    }
    return { valor: 'https://www.instagram.com/' + handle };
  }

  // Un solo campo para las dos cifras, no dos: el clic derecho de Google Maps copia la pareja
  // entera de una vez, así que un campo es un pegado y desaparece el error clásico de invertir
  // latitud y longitud. Se aceptan las tres formas con las que la gente pega esto:
  //   A "40.435710, -3.680560"   punto decimal, que es lo que copia Google
  //   B "40,435710, -3,680560"   coma decimal (Excel y Windows en español), separadas por coma,
  //     "40,435710 -3,680560"    por espacio o por punto y coma
  // Se prueba A primero: con coma decimal, A no puede casar por accidente (su segundo número
  // tendría que terminar ahí mismo), así que no hay ambigüedad entre las dos formas.
  function normalizarCoordenadas(v) {
    var t = limpiar(v).replace(/[()]/g, '').trim();
    var lat = NaN, lng = NaN, m;
    if ((m = /^(-?\d{1,3}(?:\.\d+)?)\s*[,;]\s*(-?\d{1,3}(?:\.\d+)?)$/.exec(t))) {
      lat = parseFloat(m[1]);
      lng = parseFloat(m[2]);
    } else if ((m = /^(-?\d{1,3},\d+)\s*[,;\s]\s*(-?\d{1,3},\d+)$/.exec(t))) {
      lat = parseFloat(m[1].replace(',', '.'));
      lng = parseFloat(m[2].replace(',', '.'));
    }
    if (!isFinite(lat) || !isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return { error: 'no entiendo estas coordenadas; tienen que ser dos números como 40.435710, -3.680560' };
    }
    if (lat < ESPANA_LAT_MIN || lat > ESPANA_LAT_MAX || lng < ESPANA_LNG_MIN || lng > ESPANA_LNG_MAX) {
      return { error: 'las coordenadas caen fuera de España; comprueba que no has pegado la longitud antes que la latitud' };
    }
    return { valor: { lat: lat, lng: lng } };
  }

  // Lee el blob que imprime el Liquid. Un JSON corrupto NUNCA puede tumbar la página: se
  // degrada a "sin bloques" —es decir, a la página que hay hoy en producción— y se deja
  // constancia en el panel del editor y en diagnostico().
  function leerBloques() {
    var el = document.getElementById('ndk-farmacias-bloques');
    if (!el) return { bloques: [], error: null };
    try {
      var datos = JSON.parse(el.textContent);
      if (!Array.isArray(datos)) throw new Error('el contenido no es una lista');
      return { bloques: datos, error: null };
    } catch (e) {
      return { bloques: [], error: 'No se han podido leer los bloques de la sección (' + e.message + '); la página está mostrando solo las farmacias del Excel.' };
    }
  }

  function fusionar(base, lectura) {
    var avisos = [];
    var resumen = {
      base: base.length, bloques: (lectura.bloques || []).length,
      corregidas: 0, nuevas: 0, ocultas: 0, descartados: 0
    };

    function avisar(severidad, bloque, texto, id) {
      avisos.push({
        severidad: severidad,
        posicion: bloque ? bloque.posicion : null,
        bloqueId: bloque ? bloque.bloqueId : null,
        id: id || null,
        texto: texto
      });
    }

    if (lectura.error) avisar('error', null, lectura.error);

    var bloques = lectura.bloques || [];
    // Salida rápida y literal: mismo array, mismos objetos, cero trabajo.
    if (!bloques.length) return { farmacias: base, avisos: avisos, resumen: resumen };

    // Grafías canónicas de las comunidades tal y como vienen del Excel.
    // agruparPorComunidad() usa la cadena CRUDA como clave del grupo, sin normalizar: un
    // "Madrid" escrito a mano junto a los "MADRID" del Excel abriría un segundo apartado
    // duplicado en la lista. Aquí se compara sin acentos ni mayúsculas y se adopta la grafía
    // del Excel, así que ni el select del schema ni un cambio de estilo en build.py pueden
    // partir un grupo en dos.
    var comunidades = {};
    base.forEach(function (f) {
      var clave = normalizarTexto(f.comunidad);
      if (f.comunidad && !comunidades[clave]) comunidades[clave] = f.comunidad;
    });

    // Aplica los campos de un bloque sobre un registro. SEMÁNTICA DE PARCHE, no de reemplazo:
    //   campo vacío  -> se conserva lo que hubiera (en una alta, queda a null)
    //   campo "-"    -> se borra a propósito
    //   cualquier otro valor -> sustituye, tras pasar por su normalizador
    // El vacío conserva y no borra porque el Theme Editor no distingue "no tocado" de "vaciado
    // a propósito": todos los campos de un bloque recién creado nacen vacíos. Con semántica de
    // reemplazo, crear un bloque para arreglar una tilde del nombre borraría teléfono,
    // dirección, CP y coordenadas de esa farmacia, y no se vería el destrozo hasta que un
    // cliente no encontrase la chincheta. El coste del error inverso es aprender un guion que
    // está documentado en la ayuda del propio campo.
    function aplicarCampos(reg, b) {
      var id = b._id;

      var nombre = limpiar(b.nombre);
      if (nombre === '-') {
        avisar('aviso', b, 'el nombre no admite un guion: una tarjeta sin título no se puede leer', id);
      } else if (nombre) {
        reg.nombre = nombre;
      }

      var comunidad = limpiar(b.comunidad);
      if (comunidad === '-') {
        avisar('aviso', b, 'la comunidad no admite un guion: sin ella la farmacia cae en un apartado «Otras»', id);
      } else if (comunidad) {
        var canonica = comunidades[normalizarTexto(comunidad)];
        if (canonica) {
          reg.comunidad = canonica;
        } else {
          reg.comunidad = comunidad.toLocaleUpperCase('es');
          avisar('aviso', b, '«' + reg.comunidad + '» no coincide con ninguna comunidad del Excel: abrirá un apartado nuevo en la lista', id);
        }
      }

      ['direccion', 'ciudad', 'provincia'].forEach(function (clave) {
        var v = limpiar(b[clave]);
        if (v === '-') reg[clave] = null;
        else if (v) reg[clave] = v;
      });

      [['cp', normalizarCP], ['telefono', normalizarTelefono], ['instagram', normalizarInstagram]].forEach(function (par) {
        var clave = par[0];
        var v = limpiar(b[clave]);
        if (v === '-') { reg[clave] = null; return; }
        if (!v) return;
        var r = par[1](v);
        if (r.error) avisar('aviso', b, r.error + '; se deja como estaba', id);
        else reg[clave] = r.valor;
      });

      // Las coordenadas son un par indivisible: o entran las dos o no entra ninguna.
      var coords = limpiar(b.coordenadas);
      if (coords === '-') {
        reg.lat = null;
        reg.lng = null;
        reg.precision = null;
      } else if (coords) {
        var rc = normalizarCoordenadas(coords);
        if (rc.error) {
          // Incluye el rechazo por caer fuera de España (Tarea 11 #1): reg.lat/lng no se tocan,
          // así que la farmacia se queda exactamente como si este campo no se hubiera rellenado
          // —con la coordenada buena del Excel si venía de un «corregir», o sin coordenadas si
          // venía de un «añadir» (ver construirNueva, que ya avisa de ese caso)—, nunca con un
          // pin plantado en un punto inventado.
          avisar('aviso', b, rc.error + '; se dejan como estaban', id);
        } else {
          reg.lat = rc.valor.lat;
          reg.lng = rc.valor.lng;
          reg.precision = 'manual'; // todo lo que entra por el editor es manual por definición
        }
      }
    }

    // Copia con las 14 claves exactas y ningún resto de runtime (_distanciaKm). No se muta
    // nunca el objeto original de window.NDK_FARMACIAS.
    function parchear(original, b) {
      var reg = {};
      CLAVES_REGISTRO.forEach(function (clave) {
        reg[clave] = original[clave] === undefined ? null : original[clave];
      });
      aplicarCampos(reg, b);
      return reg;
    }

    function construirNueva(b) {
      var reg = {};
      CLAVES_REGISTRO.forEach(function (clave) { reg[clave] = null; });
      reg.id = b._id;
      aplicarCampos(reg, b);
      if (!reg.nombre) {
        avisar('error', b, 'la farmacia nueva necesita un nombre', b._id);
        return null;
      }
      if (!reg.comunidad) {
        avisar('error', b, '«' + reg.nombre + '» necesita una comunidad autónoma: sin ella se agrupa bajo «Otras» a la vista del visitante', b._id);
        return null;
      }
      // Sin coordenadas NO se descarta: la página existe para responder "¿tengo Naturadika
      // cerca?", y una ficha con nombre, dirección y teléfono ya lo responde. Sale en la lista
      // (tieneCoordenadas ya le quita el botón "Cómo llegar" y la ordenación por distancia le
      // asigna Infinity, así que cae al final sin romper nada) y el fundador ve el aviso.
      if (!tieneCoordenadas(reg)) {
        avisar('aviso', b, '«' + reg.nombre + '» no tiene coordenadas: sale en la lista pero no tiene chincheta en el mapa', b._id);
      }
      return reg;
    }

    // 1) Resolver identificadores y colisiones entre bloques.
    // Ante dos bloques con el mismo identificador GANA EL PRIMERO en orden de editor. Es la
    // única regla bajo la cual añadir un bloque al final nunca puede cambiar en silencio lo que
    // hacía un bloque ya existente; y como duplicar un bloque en Shopify inserta la copia justo
    // debajo, con "gana el último" la copia se apoderaría del original sin que se note.
    var porId = {};
    var aceptados = [];
    bloques.forEach(function (b) {
      if (b.tipo !== 'anadir' && b.tipo !== 'corregir' && b.tipo !== 'ocultar') {
        avisar('error', b, 'tipo de bloque desconocido: ' + b.tipo);
        resumen.descartados++;
        return;
      }
      var id = slugificar(limpiar(b.farmacia_id));
      if (!id && b.tipo === 'anadir') id = slugificar(limpiar(b.nombre));
      if (!id) {
        avisar('error', b, b.tipo === 'anadir'
          ? 'bloque de alta sin rellenar: hace falta al menos el nombre'
          : 'falta el identificador de la farmacia; cópialo de la lista de abajo');
        resumen.descartados++;
        return;
      }
      if (porId[id]) {
        avisar('error', b, 'ya hay otro bloque para «' + id + '» en la posición ' + porId[id].posicion + '; este se ignora entero', id);
        resumen.descartados++;
        return;
      }
      b._id = id;
      porId[id] = b;
      aceptados.push(b);
    });

    // 2) Índice del Excel. build.py aborta ante identificadores repetidos, así que esto es una
    // red defensiva, no un caso esperado.
    var indiceBase = {};
    base.forEach(function (f, i) {
      if (indiceBase[f.id] === undefined) indiceBase[f.id] = i;
      else avisar('aviso', null, 'el Excel trae dos farmacias con el identificador «' + f.id + '»; se usa la primera', f.id);
    });

    // 3) Aplicar. El array de salida arranca como copia del ORDEN del Excel: corregir jamás
    // reordena (una farmacia corregida se queda en su sitio) y las altas van al final.
    var salida = base.slice();
    var ocultos = {};
    var nuevas = [];

    aceptados.forEach(function (b) {
      var id = b._id;
      var indice = indiceBase[id];
      var existe = indice !== undefined;

      if (b.tipo === 'ocultar') {
        if (!existe) {
          avisar('error', b, 'no existe ninguna farmacia con el identificador «' + id + '»; no hay nada que ocultar', id);
          resumen.descartados++;
          return;
        }
        ocultos[id] = true;
        resumen.ocultas++;
        return;
      }

      if (b.tipo === 'corregir') {
        // Esta es la razón de ser de tener tipos separados: un identificador mal escrito en una
        // corrección NO crea nunca una farmacia fantasma a medio rellenar en el mapa.
        if (!existe) {
          avisar('error', b, 'no existe ninguna farmacia con el identificador «' + id + '». Si lo que querías era añadirla, usa el bloque «Añadir una farmacia»', id);
          resumen.descartados++;
          return;
        }
        salida[indice] = parchear(salida[indice], b);
        resumen.corregidas++;
        return;
      }

      // anadir: si el identificador ya existe se aplica como corrección, nunca se inserta dos
      // veces. overlaysPinPorId está indexado por id y un duplicado se comería una chincheta,
      // y el data-id de la tarjeta dejaría de ser único.
      if (existe) {
        avisar('aviso', b, '«' + id + '» ya viene del Excel: el bloque se ha aplicado como corrección en vez de duplicarla', id);
        salida[indice] = parchear(salida[indice], b);
        resumen.corregidas++;
        return;
      }
      var nueva = construirNueva(b);
      if (!nueva) {
        resumen.descartados++;
        return;
      }
      nuevas.push(nueva);
      resumen.nuevas++;
    });

    // 4) Ocultar es ELIMINAR del array, antes de agrupar, contar y buscar: así el mapa, la
    // lista, los contadores de cada comunidad, el buscador y la geolocalización coinciden
    // siempre, sin tocar ninguna de esas funciones.
    var farmacias = salida.filter(function (f) { return !ocultos[f.id]; }).concat(nuevas);
    return { farmacias: farmacias, avisos: avisos, resumen: resumen };
  }

  FUSION = fusionar((window.NDK_FARMACIAS && window.NDK_FARMACIAS.farmacias) || [], leerBloques());
  TODAS = FUSION.farmacias;

  // ---------- contenido compartido (InfoWindow de escritorio y ficha inferior de mobile) ----------

  function urlComoLlegar(f) {
    return 'https://www.google.com/maps/dir/?api=1&destination=' + f.lat + ',' + f.lng;
  }

  // El botón "Web" se quitó a propósito (Tarea 9 #2): decisión del fundador, no una limpieza de
  // datos. `f.web` SIGUE llegando en window.NDK_FARMACIAS y sigue en la lista blanca de
  // tools/farmacias — no tocar eso. Esto solo deja de pintar el botón; Instagram se mantiene.
  function botonesHTML(f) {
    var html = '';
    if (tieneCoordenadas(f)) {
      html += '<a class="ndk-farmacias__boton ndk-farmacias__boton--llegar" href="' + urlComoLlegar(f) + '" target="_blank" rel="noopener noreferrer">Cómo llegar</a>';
    }
    if (f.telefono) {
      html += '<a class="ndk-farmacias__boton ndk-farmacias__boton--llamar" href="tel:' + escapeHtml(String(f.telefono)) + '">Llamar</a>';
    }
    if (f.instagram) {
      html += '<a class="ndk-farmacias__boton ndk-farmacias__boton--instagram" href="' + escapeHtml(f.instagram) + '" target="_blank" rel="noopener noreferrer">Instagram</a>';
    }
    return html;
  }

  // Partes de la dirección (calle, y CP + ciudad), listas para unir con puntos medios. Único
  // sitio donde se construyen: antes el popup/ficha las unía con coma ("Calle de Castelló, 110,
  // 28006 Madrid") y la tarjeta de la lista con puntos medios ("Calle de Castelló, 110 · 28006
  // Madrid") para la MISMA farmacia — incoherencia detectada en la Tarea 9 #3. Compartir esta
  // función es lo que impide que vuelvan a divergir.
  function direccionPartes(f) {
    var partes = [];
    if (f.direccion) partes.push(escapeHtml(f.direccion));
    var ciudad = (String(f.cp == null ? '' : f.cp) + ' ' + capitalizarPalabras(f.ciudad)).trim();
    if (ciudad) partes.push(escapeHtml(ciudad));
    return partes;
  }

  // Contenido compartido por la InfoWindow anclada (>=750px) y la ficha inferior (<750px, ver
  // más abajo): mismo nombre, dirección y botones, un único sitio donde generarlos.
  function contenidoFarmaciaHTML(f) {
    return '<p class="ndk-farmacias__popup-nombre">' + escapeHtml(f.nombre) + '</p>' +
      '<p class="ndk-farmacias__popup-direccion">' + direccionPartes(f).join(' · ') + '</p>' +
      '<div class="ndk-farmacias__popup-acciones">' + botonesHTML(f) + '</div>';
  }

  function popupHTML(f) {
    return '<div class="ndk-farmacias__popup">' + contenidoFarmaciaHTML(f) + '</div>';
  }

  function iconoPinHTML() {
    return '<span class="ndk-farmacias__pin"></span>';
  }

  // Un grupo dibujado como burbuja solo enseña un número: sin esto, un lector de pantalla
  // anunciaría «12» y nada más. El mismo texto va también en `title` para quien pasa el ratón
  // por encima en escritorio, que es donde más se echa de menos el nombre de la provincia.
  function etiquetaAccesibleGrupo(nombre, contador, aproximado) {
    var texto = contador + (contador === 1 ? ' farmacia' : ' farmacias');
    if (nombre) texto += (aproximado ? ' en la zona de ' : ' en ') + capitalizarPalabras(nombre);
    return texto;
  }

  // Dos formas para el mismo grupo, según el zoom (ver construirClusters):
  //   conNombre=false -> burbuja circular con solo el número (vista nacional)
  //   conNombre=true  -> píldora con el nombre en mayúsculas encima del número
  function iconoClusterHTML(nombre, contador, conNombre, aproximado) {
    var etiqueta = escapeHtml(etiquetaAccesibleGrupo(nombre, contador, aproximado));
    var clases = 'ndk-farmacias__cluster' + (conNombre && nombre ? '' : ' ndk-farmacias__cluster--burbuja');
    var html = '<span class="' + clases + '" role="button" tabindex="0" aria-label="' + etiqueta + '" title="' + etiqueta + '">';
    if (conNombre && nombre) {
      html += '<span class="ndk-farmacias__cluster-nombre">' + escapeHtml(capitalizarPalabras(nombre)) + '</span>';
    }
    return html + '<span class="ndk-farmacias__cluster-contador">' + contador + '</span></span>';
  }

  // Tesela cuadrada de la tarjeta (cruz de farmacia). SVG inline a propósito: nada de imágenes
  // remotas ni de una petición extra por tarjeta.
  function iconoFarmaciaSVG() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="M9.4 3h5.2v6.4H21v5.2h-6.4V21H9.4v-6.4H3V9.4h6.4V3Z" fill="currentColor"/>' +
      '</svg>';
  }

  // ---------- ficha inferior (mobile) ----------
  // Por debajo de 750px una InfoWindow anclada al pin no cabe en el ancho del contenedor del
  // mapa (se sale del recorte con `overflow: hidden` de .ndk-farmacias__mapa-envoltorio, de
  // ahí el fallo con el nombre de la farmacia cortado que costó arreglar). En su lugar, al
  // tocar un pin mostramos una ficha propia anclada al fondo del mapa, con el mismo contenido
  // (contenidoFarmaciaHTML). A partir de 750px se usa la InfoWindow nativa de Google
  // (ver abrirInfoWindow, dentro de inicializarMapa).

  var UMBRAL_MOBILE_MQ = window.matchMedia ? window.matchMedia('(max-width: 749.98px)') : null;

  function esMobile() {
    return UMBRAL_MOBILE_MQ ? UMBRAL_MOBILE_MQ.matches : window.innerWidth < 750;
  }

  var elFicha = document.getElementById('ndk-farmacias-ficha');
  var elFichaContenido = document.getElementById('ndk-farmacias-ficha-contenido');
  var elFichaCerrar = document.getElementById('ndk-farmacias-ficha-cerrar');
  var CLASE_FICHA_VISIBLE = 'ndk-farmacias__ficha--visible';
  var idPinSeleccionado = null;

  // Un click dentro de la ficha (p.ej. en "Llamar") es un descendiente de #ndk-farmacias-mapa,
  // el mismo contenedor sobre el que se inicializa el mapa de Google: sin cortar la
  // propagación aquí, ese click burbujea hasta el listener de 'click' del mapa (más abajo,
  // dentro de inicializarMapa) y lo interpretaría como un click en el mapa, cerrando la
  // ficha que se acaba de abrir. Es el equivalente manual de lo que antes hacía
  // `L.DomEvent.disableClickPropagation` en la versión con Leaflet.
  if (elFicha) {
    elFicha.addEventListener('click', function (e) {
      e.stopPropagation();
    });
  }

  function quitarSeleccionPin() {
    if (idPinSeleccionado != null) {
      var overlay = overlaysPinPorId[idPinSeleccionado];
      var pin = overlay && overlay.div && overlay.div.querySelector('.ndk-farmacias__pin');
      if (pin) pin.classList.remove('ndk-farmacias__pin--seleccionado');
    }
    idPinSeleccionado = null;
  }

  function marcarSeleccionPin(f) {
    if (idPinSeleccionado === f.id) return;
    quitarSeleccionPin();
    idPinSeleccionado = f.id;
    var overlay = overlaysPinPorId[f.id];
    var pin = overlay && overlay.div && overlay.div.querySelector('.ndk-farmacias__pin');
    if (pin) pin.classList.add('ndk-farmacias__pin--seleccionado');
  }

  // Deja sitio a la franja de copyright/Términos de Google (esquina inferior, obligatoria por
  // licencia) para que la ficha, a ancho casi completo, no la tape. El nombre de clase interno
  // de esa franja (`.gm-style-cc`) no forma parte de la API pública de Google y puede cambiar
  // sin aviso entre versiones del SDK — a diferencia de `.leaflet-bottom.leaflet-right`, que sí
  // era una convención pública de Leaflet. Por eso aquí se usa solo como mejora progresiva
  // (si se encuentra, se mide su alto real) y nunca como única fuente de verdad: si no
  // aparece, se cae a un offset fijo ya de por sí generoso para ese texto.
  var FICHA_OFFSET_MINIMO = 32;

  function ajustarOffsetFicha() {
    if (!elFicha) return;
    var barra = elMapa && elMapa.querySelector('.gm-style-cc');
    var alturaBarra = barra ? barra.offsetHeight : 0;
    elFicha.style.bottom = Math.max(FICHA_OFFSET_MINIMO, alturaBarra + 12) + 'px';
  }

  function abrirFicha(f) {
    if (!elFicha || !elFichaContenido) return;
    elFichaContenido.innerHTML = contenidoFarmaciaHTML(f);
    elFicha.classList.add(CLASE_FICHA_VISIBLE);
    elFicha.setAttribute('aria-hidden', 'false');
    marcarSeleccionPin(f);
    // La ficha solo existe/se abre por debajo de 750px (ver el media query en el CSS de esta
    // sección) y, abierta, tapa los controles de zoom de Google en ese ancho — se apartan
    // mientras esté visible (Tarea 9 #8; implementación en mapaAPI.moverControlZoom).
    mapaAPI.moverControlZoom(true);
  }

  function cerrarFicha() {
    if (!elFicha || !elFicha.classList.contains(CLASE_FICHA_VISIBLE)) return;
    elFicha.classList.remove(CLASE_FICHA_VISIBLE);
    elFicha.setAttribute('aria-hidden', 'true');
    quitarSeleccionPin();
    mapaAPI.moverControlZoom(false);
  }

  if (elFichaCerrar) {
    elFichaCerrar.addEventListener('click', cerrarFicha);
  }

  // ---------- lista agrupada por comunidad ----------

  function agruparPorComunidad(lista) {
    var grupos = {};
    lista.forEach(function (f) {
      var clave = f.comunidad || 'Otras';
      if (!grupos[clave]) grupos[clave] = [];
      grupos[clave].push(f);
    });
    return Object.keys(grupos)
      .map(function (clave) { return { comunidad: clave, items: grupos[clave] }; })
      .sort(function (a, b) {
        if (b.items.length !== a.items.length) return b.items.length - a.items.length;
        return a.comunidad.localeCompare(b.comunidad, 'es');
      });
  }

  // Patrón de tarjeta del lenguaje de diseño: tesela cuadrada a la izquierda, bloque de texto
  // en medio (nombre en negrita y atributos secundarios en gris separados por PUNTOS MEDIOS,
  // en una sola línea en vez de los dos párrafos apilados de antes) y el dato destacado —la
  // distancia— arriba a la derecha.
  function tarjetaHTML(f, opciones) {
    var distanciaHTML = '';
    if (opciones && opciones.mostrarDistancia && typeof f._distanciaKm === 'number' && isFinite(f._distanciaKm)) {
      distanciaHTML = '<p class="ndk-farmacias__tarjeta-distancia">' + formatearDistancia(f._distanciaKm) + '</p>';
    }

    var secundarios = direccionPartes(f);
    var secundariosHTML = secundarios.length
      ? '<p class="ndk-farmacias__tarjeta-direccion">' + secundarios.join(' · ') + '</p>'
      : '';

    return '<article class="ndk-farmacias__tarjeta" data-id="' + escapeHtml(f.id) + '">' +
      '<span class="ndk-farmacias__tarjeta-icono" aria-hidden="true">' + iconoFarmaciaSVG() + '</span>' +
      '<div class="ndk-farmacias__tarjeta-cuerpo">' +
        '<div class="ndk-farmacias__tarjeta-cabecera">' +
          '<p class="ndk-farmacias__tarjeta-nombre">' + escapeHtml(f.nombre) + '</p>' +
          distanciaHTML +
        '</div>' +
        secundariosHTML +
        '<div class="ndk-farmacias__tarjeta-acciones">' + botonesHTML(f) + '</div>' +
      '</div>' +
      '</article>';
  }

  function estadoVacioHTML() {
    return '<div class="ndk-farmacias__vacio">' +
      '<p class="ndk-farmacias__vacio-titulo">Aún no estamos en tu zona</p>' +
      '<p class="ndk-farmacias__vacio-texto">Puedes pedirlo online y te llega a casa.</p>' +
      '<a class="ndk-farmacias__vacio-boton" href="' + escapeHtml(CIERRE_BOTON_URL) + '">' + escapeHtml(CIERRE_BOTON_TEXTO) + '</a>' +
      '</div>';
  }

  function renderizarLista(lista, opciones) {
    if (!elLista) return;
    opciones = opciones || {};
    if (!lista.length) {
      elLista.innerHTML = estadoVacioHTML();
      return;
    }
    var html = '';
    if (opciones.plano) {
      html += '<div class="ndk-farmacias__grupo"><div class="ndk-farmacias__tarjetas">';
      lista.forEach(function (f) { html += tarjetaHTML(f, opciones); });
      html += '</div></div>';
    } else {
      agruparPorComunidad(lista).forEach(function (grupo) {
        html += '<div class="ndk-farmacias__grupo">' +
          '<h2 class="ndk-farmacias__grupo-titulo">' + escapeHtml(capitalizarPalabras(grupo.comunidad)) +
          ' <span class="ndk-farmacias__grupo-contador">' + grupo.items.length + '</span></h2>' +
          '<div class="ndk-farmacias__tarjetas">';
        grupo.items.forEach(function (f) { html += tarjetaHTML(f, opciones); });
        html += '</div></div>';
      });
    }
    elLista.innerHTML = html;
  }

  // ---------- estado + interfaz pública ----------
  // window.NdkFarmacias.filtrar y .ordenarPorDistancia ya funcionan de verdad (probables desde
  // consola); la Tarea 6 solo tenía que cablear el input del buscador y la geolocalización, y
  // la Tarea 7 (esta) solo sustituye qué pasa con el mapa por debajo, no esta capa.

  var ESTADO = {
    todas: TODAS,
    filtradas: TODAS
  };

  var elCierreResultados = document.getElementById('ndk-farmacias-cierre-resultados');
  var elAvisoProvincia = document.getElementById('ndk-farmacias-aviso-provincia');
  var UMBRAL_LEJOS_KM = 100; // por debajo de esto no añadimos el remate de compra online

  // El botón del estado vacío (más abajo, en estadoVacioHTML) tiene que llevar el MISMO destino
  // y el MISMO texto que este, que ya sale del Liquid con el ajuste del editor resuelto —o su
  // valor de fábrica si el ajuste está en blanco (ver ndkf_cierre_boton_url/_texto en
  // sections/ndk-farmacias-section.liquid)—. Leerlos de aquí, en vez de repetir el fallback en
  // JS, es lo que impide que los dos botones diverjan en cuanto el fundador cambie el ajuste
  // desde el editor (Tarea 11 #3): antes de esto, el del estado vacío llevaba "/collections/all"
  // cableado a fuego y solo coincidía con el otro botón por casualidad.
  var elCierreBoton = document.getElementById('ndk-farmacias-cierre-resultados-boton');
  var CIERRE_BOTON_URL = elCierreBoton ? elCierreBoton.getAttribute('href') : '/collections/all';
  var CIERRE_BOTON_TEXTO = elCierreBoton ? elCierreBoton.textContent : 'Comprar online';

  // mostrar, si se pasa explícito (true/false), gana sobre el criterio por defecto
  // (mostrar siempre que haya resultados — la caja de "cómpralo online" es un remate
  // que convive con la lista, no un sustituto del estado vacío).
  function actualizarCierre(lista, mostrar) {
    if (!elCierreResultados) return;
    var visible = typeof mostrar === 'boolean' ? mostrar : !!(lista && lista.length);
    elCierreResultados.hidden = !visible;
  }

  function mostrarAvisoProvincia(nombreProvincia) {
    if (!elAvisoProvincia) return;
    elAvisoProvincia.innerHTML = '<p class="ndk-farmacias__aviso-provincia-texto">' +
      '<strong>No tenemos farmacia en ' + escapeHtml(nombreProvincia) + ' todavía.</strong> Estas son las más cercanas:' +
      '</p>';
    elAvisoProvincia.hidden = false;
  }

  function ocultarAvisoProvincia() {
    if (!elAvisoProvincia) return;
    elAvisoProvincia.hidden = true;
    elAvisoProvincia.innerHTML = '';
  }

  // ---------- búsqueda por código postal ----------
  // Un CP español válido SIEMPRE devuelve farmacias, ordenadas de más cerca a más lejos:
  //   1. Coincidencia exacta de CP -> esas primero, seguidas del resto de la provincia por distancia.
  //   2. Sin exacta pero con farmacias en la provincia (mismo prefijo de 2 dígitos) -> esas, por distancia.
  //   3. Provincia sin ninguna farmacia -> TODAS las farmacias de España, ordenadas por distancia a la
  //      capital de esa provincia (window.NDK_PROVINCIAS, assets/ndk-farmacias-provincias.js).
  // Solo si el prefijo no está en NDK_PROVINCIAS (CP no reconocible, p.ej. 99999) se trata como
  // texto sin coincidencias y cae al estado vacío — nunca para un CP español real.

  function farmaciasPorPrefijo(prefijo) {
    return ESTADO.todas.filter(function (f) { return String(f.cp).slice(0, 2) === prefijo; });
  }

  function ordenarPorDistanciaAPunto(lista, lat, lng) {
    lista.forEach(function (f) {
      f._distanciaKm = tieneCoordenadas(f) ? distanciaHaversine(lat, lng, f.lat, f.lng) : Infinity;
    });
    return lista.slice().sort(function (a, b) { return a._distanciaKm - b._distanciaKm; });
  }

  function buscarPorCP(cp) {
    var prefijo = cp.slice(0, 2);
    var provincia = window.NDK_PROVINCIAS && window.NDK_PROVINCIAS[prefijo];
    var enProvincia = farmaciasPorPrefijo(prefijo);

    if (enProvincia.length) {
      var exactas = enProvincia.filter(function (f) { return f.cp === cp; });
      var resto = enProvincia.filter(function (f) { return f.cp !== cp; });
      if (provincia) {
        resto = ordenarPorDistanciaAPunto(resto, provincia[0], provincia[1]);
      }
      return { lista: exactas.concat(resto), nivel: exactas.length ? 'exacta' : 'provincia', provincia: provincia };
    }

    if (provincia) {
      return { lista: ordenarPorDistanciaAPunto(ESTADO.todas.slice(), provincia[0], provincia[1]), nivel: 'fallback', provincia: provincia };
    }

    return { lista: [], nivel: 'invalida', provincia: null };
  }

  // ---------- capa de mapa (Google Maps) ----------
  // Todo lo que toca `google.maps.*` vive a partir de aquí y solo se activa si conseguimos
  // cargar la librería 'maps' de Google vía google.maps.importLibrary (llamada más abajo, en
  // la sección de inicialización, cuando hay bootstrap loader insertado — ver
  // sections/ndk-farmacias-section.liquid). Mientras tanto —o si nunca hay clave— `mapaAPI` se
  // queda en sus versiones "no-op": el resto del archivo (filtrar, ordenarPorDistancia, el
  // buscador, la geolocalización) llama siempre a `mapaAPI.*`, nunca a `google.*` directamente,
  // así que funciona igual con o sin mapa.

  var overlaysPinPorId = {};
  var overlaysPinLista = []; // [{ f, overlay }], orden estable para window.NdkFarmacias.marcadores

  // Encuadre pedido ANTES de que Google terminase de cargar: la lista, el buscador y la
  // geolocalización funcionan desde el primer render, así que el visitante puede haber buscado
  // (o haber pulsado "usar mi ubicación") mientras la API de Google todavía venía por la red.
  // En vez de perder ese encuadre, la versión "no-op" de mapaAPI lo anota y inicializarMapa lo
  // reproduce en cuanto el mapa existe. Si nadie pidió nada, el mapa arranca en la vista
  // nacional exacta — ver aplicarEncuadreInicial() dentro de inicializarMapa.
  var encuadrePendiente = null; // { tipo: 'recolocar' | 'punto', ... } | null

  var mapaAPI = {
    actualizarCapasVisibles: function () {},
    recolocarMapa: function (puntos, opciones) {
      encuadrePendiente = { tipo: 'recolocar', puntos: puntos, opciones: opciones || {} };
    },
    centrarEnPunto: function (lat, lng, farmaciaMasCercana) {
      encuadrePendiente = { tipo: 'punto', lat: lat, lng: lng, farmacia: farmaciaMasCercana };
    },
    // No-op hasta que el mapa exista; inicializarMapa la sustituye por la real (Tarea 9 #8).
    moverControlZoom: function () {}
  };

  function inicializarMapa() {
    if (!window.google || !window.google.maps || !elMapaGoogle) return;

    var mapa = new google.maps.Map(elMapaGoogle, {
      center: CENTRO_ESPANA,
      zoom: ZOOM_INICIAL,
      zoomControl: true,
      // Posición explícita (no el default sin más) para tener una base conocida a la que volver
      // en moverControlZoom() cuando se cierra la ficha inferior — ver más abajo (Tarea 9 #8).
      zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
      gestureHandling: 'cooperative', // como scrollWheelZoom:false en Leaflet: no atrapa el scroll de la página
      // Único ajuste de aspecto: ocultar los puntos de interés comerciales para que no compitan
      // visualmente con los pines de farmacia. Nada de estilos oscuros ni de saturación — el
      // resto es el Google Maps por defecto, que es precisamente el aspecto reconocible que se
      // quería al migrar desde Leaflet.
      styles: [{ featureType: 'poi.business', stylers: [{ visibility: 'off' }] }]
    });

    // ---------- overlay HTML propio ----------
    // Google no ofrece un equivalente a L.divIcon sin usar Advanced Markers (que exigen un
    // Map ID de Google Cloud y cargar la librería `marker`, fuera del alcance de esta tarea).
    // Un OverlayView es la forma estándar de anclar un <div> propio a una coordenada del mapa:
    // Google lo posiciona (onAdd/draw/onRemove) dentro de su propio `overlayMouseTarget`, la
    // pane pensada exactamente para overlays interactivos (los Marker de Google usan la misma),
    // por lo que sus clicks no llegan a disparar el 'click' del mapa, igual que pasaba con los
    // marcadores de Leaflet. Reutiliza las clases .ndk-farmacias__pin / __cluster ya existentes
    // como contenido; el posicionamiento fino (qué punto del icono queda sobre la coordenada)
    // se resuelve por CSS con `transform` en .ndk-farmacias__marcador-pin / -cluster, no aquí.
    function NdkOverlay(mapaDestino, posicion, className, html, onClick) {
      this.posicion = (posicion instanceof google.maps.LatLng) ? posicion : new google.maps.LatLng(posicion.lat, posicion.lng);
      this.className = className;
      this.html = html;
      this.onClick = onClick;
      this.div = null;
      this.setMap(mapaDestino);
    }
    NdkOverlay.prototype = Object.create(google.maps.OverlayView.prototype);

    NdkOverlay.prototype.onAdd = function () {
      var div = document.createElement('div');
      div.className = this.className;
      div.innerHTML = this.html;
      if (this.onClick) {
        var self = this;
        div.addEventListener('click', function (e) {
          e.stopPropagation();
          self.onClick(e);
        });
        // Los grupos son `role="button"` con tabindex (ver iconoClusterHTML): sin esto se
        // podrían enfocar con el teclado pero no activar.
        div.addEventListener('keydown', function (e) {
          if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
          e.preventDefault();
          e.stopPropagation();
          self.onClick(e);
        });
      }
      this.div = div;
      this.getPanes().overlayMouseTarget.appendChild(div);
    };

    NdkOverlay.prototype.draw = function () {
      var proj = this.getProjection();
      if (!proj || !this.div) return;
      var punto = proj.fromLatLngToDivPixel(this.posicion);
      this.div.style.left = punto.x + 'px';
      this.div.style.top = punto.y + 'px';
    };

    NdkOverlay.prototype.onRemove = function () {
      if (this.div && this.div.parentNode) this.div.parentNode.removeChild(this.div);
      this.div = null;
    };

    // ---------- InfoWindow (escritorio, >=750px) ----------

    var infoWindow = new google.maps.InfoWindow({ maxWidth: 260 });

    function abrirInfoWindow(f) {
      infoWindow.setContent(popupHTML(f));
      infoWindow.setPosition({ lat: f.lat, lng: f.lng });
      infoWindow.open(mapa);
    }

    // Marcadores individuales: se crean una sola vez y se muestran/ocultan según filtro y zoom.
    TODAS.forEach(function (f) {
      if (!tieneCoordenadas(f)) return;
      var overlay = new NdkOverlay(null, { lat: f.lat, lng: f.lng }, 'ndk-farmacias__marcador-pin', iconoPinHTML(), function () {
        if (esMobile()) {
          abrirFicha(f);
        } else {
          infoWindow.close();
          abrirInfoWindow(f);
        }
      });
      overlaysPinPorId[f.id] = overlay;
      overlaysPinLista.push({ f: f, overlay: overlay });
    });

    // ---------- agrupación propia por provincia para el mapa (sin librería de clustering) ----------

    function centroide(items) {
      var sumaLat = 0, sumaLng = 0;
      items.forEach(function (f) {
        sumaLat += f.lat;
        sumaLng += f.lng;
      });
      return { lat: sumaLat / items.length, lng: sumaLng / items.length };
    }

    var overlaysCluster = [];
    var idsVisibles = {};
    var modoClusters = true; // true = zoom bajo (agrupado), false = zoom alto (pines individuales)
    var listaAgrupada = []; // última lista con la que se construyeron los grupos, para rehacerlos al cambiar el zoom
    var zoomAgrupado = null; // zoom entero con el que se construyeron: evita rehacer 14 overlays en cada tick

    // Cómo se nombra un grupo fusionado. Si todas sus farmacias comparten comunidad, esa
    // comunidad es una etiqueta exacta ("Galicia" para A Coruña + Pontevedra). Si mezcla
    // comunidades (Madrid + Cuenca, Málaga + Ceuta) no hay nombre exacto, pero dejar solo un
    // número tampoco ayuda a nadie: se usa la provincia con más farmacias del grupo y se
    // anuncia como aproximada ("en la zona de"), que es lo que de verdad describe la burbuja.
    function nombrarGrupoFusionado(items) {
      var primera = items[0] && items[0].comunidad;
      var mismaComunidad = !!primera;
      var conteo = {};
      var dominante = null;
      items.forEach(function (f) {
        if (!primera || normalizarTexto(f.comunidad) !== normalizarTexto(primera)) mismaComunidad = false;
        var prov = f.provincia || f.comunidad;
        if (!prov) return;
        var clave = normalizarTexto(prov);
        conteo[clave] = (conteo[clave] || 0) + 1;
        if (!dominante || conteo[clave] > conteo[normalizarTexto(dominante)]) dominante = prov;
      });
      if (mismaComunidad) return { etiqueta: primera, aproximado: false };
      return { etiqueta: dominante, aproximado: true };
    }

    // Fusión codiciosa EN PÍXELES, de mayor a menor número de farmacias: el grupo grande
    // absorbe a los pequeños cuya etiqueta se le echa encima, nunca al revés. La prueba no es
    // una distancia fija sino el solape de las dos HUELLAS (ver huellaGrupo): una burbuja de
    // 34px y una píldora de 170px no estorban lo mismo, y usar un único radio para ambas
    // dejaba las píldoras pisándose a zoom 6. Así el solape se resuelve por construcción a
    // cualquier zoom y con cualquier número de provincias en el dataset —que va a crecer—, en
    // vez de por un ajuste fino que se rompe en la siguiente alta. Al acercarse, la distancia
    // en píxeles crece y los grupos se separan solos.
    function fusionarGruposCercanos(grupos, zoom, conNombre) {
      var ordenados = grupos.slice().sort(function (a, b) { return b.items.length - a.items.length; });
      ordenados.forEach(function (g) {
        g.px = proyectarPixel(g.centro.lat, g.centro.lng, zoom);
        g.huella = huellaGrupo(g.etiqueta, conNombre);
      });
      var absorbidos = {};
      var resultado = [];
      ordenados.forEach(function (grupo, i) {
        if (absorbidos[i]) return;
        var items = grupo.items.slice();
        var huboFusion = false;
        ordenados.forEach(function (otro, j) {
          if (j <= i || absorbidos[j]) return;
          var dx = Math.abs(grupo.px.x - otro.px.x);
          var dy = Math.abs(grupo.px.y - otro.px.y);
          var separadosEnX = dx >= (grupo.huella.ancho + otro.huella.ancho) / 2 + SEPARACION_GRUPOS_PX;
          var separadosEnY = dy >= (grupo.huella.alto + otro.huella.alto) / 2 + SEPARACION_GRUPOS_PX;
          if (separadosEnX || separadosEnY) return;
          absorbidos[j] = true;
          items = items.concat(otro.items);
          huboFusion = true;
        });
        var nombrado = huboFusion ? nombrarGrupoFusionado(items) : { etiqueta: grupo.etiqueta, aproximado: false };
        resultado.push({
          etiqueta: nombrado.etiqueta,
          aproximado: nombrado.aproximado,
          items: items,
          centro: huboFusion ? centroide(items) : grupo.centro
        });
      });
      return resultado;
    }

    function construirClusters(lista) {
      overlaysCluster.forEach(function (c) { c.setMap(null); });
      overlaysCluster = [];
      listaAgrupada = lista;
      var zoom = Math.floor(mapa.getZoom());
      zoomAgrupado = zoom;
      var conNombre = zoom >= ZOOM_GRUPO_CON_NOMBRE;

      var grupos = {};
      lista.forEach(function (f) {
        if (!tieneCoordenadas(f)) return;
        // La provincia de origen no siempre viene con el mismo acentos/mayúsculas
        // (p.ej. "MALAGA" vs "Málaga"); se agrupa por la forma normalizada para no
        // duplicar clusters de la misma provincia, y se muestra con capitalizarPalabras.
        var etiqueta = f.provincia || f.comunidad || 'Otras';
        var clave = normalizarTexto(etiqueta);
        if (!grupos[clave]) grupos[clave] = { etiqueta: etiqueta, items: [] };
        grupos[clave].items.push(f);
      });

      var burbujas = Object.keys(grupos).map(function (clave) {
        return { etiqueta: grupos[clave].etiqueta, items: grupos[clave].items, centro: centroide(grupos[clave].items) };
      });
      burbujas = fusionarGruposCercanos(burbujas, zoom, conNombre);

      // De menor a mayor: los overlays se apilan por orden de inserción dentro de la pane de
      // Google, así que el último creado queda ENCIMA. Antes se recorrían en el orden del
      // dataset y por eso «Madrid 12», la etiqueta más valiosa del mapa, acababa medio tapada
      // por Salamanca y Cuenca, con una sola farmacia cada una.
      burbujas.sort(function (a, b) { return a.items.length - b.items.length; });

      burbujas.forEach(function (grupo) {
        var overlay = new NdkOverlay(null, grupo.centro, 'ndk-farmacias__marcador-cluster', iconoClusterHTML(grupo.etiqueta, grupo.items.length, conNombre, grupo.aproximado), function () {
          var bounds = new google.maps.LatLngBounds();
          grupo.items.forEach(function (f) { bounds.extend(new google.maps.LatLng(f.lat, f.lng)); });
          encuadrarConMaxZoom(bounds, 36, ZOOM_UMBRAL_CLUSTER + 2);
        });
        overlaysCluster.push(overlay);
      });
    }

    function actualizarCapaMarcadores(lista) {
      idsVisibles = {};
      lista.forEach(function (f) { idsVisibles[f.id] = true; });
    }

    function aplicarVisibilidadPorZoom() {
      modoClusters = mapa.getZoom() < ZOOM_UMBRAL_CLUSTER;
      overlaysPinLista.forEach(function (item) {
        item.overlay.setMap(!modoClusters && idsVisibles[item.f.id] ? mapa : null);
      });
      overlaysCluster.forEach(function (c) { c.setMap(modoClusters ? mapa : null); });
    }

    function actualizarCapasVisibles(lista) {
      actualizarCapaMarcadores(lista);
      construirClusters(lista);
      aplicarVisibilidadPorZoom();
    }

    // Los grupos ahora dependen del zoom (forma de la etiqueta y umbral de fusión), no solo del
    // filtro: hay que rehacerlos también al hacer zoom. Se cachea por zoom ENTERO para no
    // reconstruir catorce overlays en cada tick de una animación de zoom.
    function alCambiarZoom() {
      var zoom = Math.floor(mapa.getZoom());
      if (zoom < ZOOM_UMBRAL_CLUSTER && zoom !== zoomAgrupado) {
        construirClusters(listaAgrupada);
      }
      aplicarVisibilidadPorZoom();
    }

    mapa.addListener('zoom_changed', alCambiarZoom);

    // Tocar el mapa fuera de un pin cierra la ficha. Los pines/clusters cortan su propia
    // propagación (ver el comentario de NdkOverlay más arriba), así que este listener solo
    // salta con clicks en el fondo del mapa, igual que con Leaflet antes.
    mapa.addListener('click', cerrarFicha);

    // ---------- recolocar el mapa tras un cambio de filtro ----------
    // Único sitio que mueve la vista del mapa cuando cambian los resultados del buscador
    // (texto, CP exacto, CP con farmacias en la provincia, o el fallback nacional), y también
    // desde la geolocalización. `fitBounds` de Google no admite un `maxZoom` directo (a
    // diferencia de Leaflet): el patrón estándar es dejar que `fitBounds` calcule el
    // encuadre y, en el primer 'idle' tras aplicarlo, recortar el zoom si se pasa. Esto es lo
    // que costó arreglar la primera vez (con Leaflet) para que una sola farmacia no dejara el
    // mapa a zoom de portal — se mantiene el mismo límite (ZOOM_MAXIMO_RESULTADO) aquí.

    function encuadrarConMaxZoom(bounds, padding, maxZoom) {
      google.maps.event.addListenerOnce(mapa, 'idle', function () {
        if (mapa.getZoom() > maxZoom) mapa.setZoom(maxZoom);
      });
      mapa.fitBounds(bounds, padding);
    }

    function moverVista(centro, zoom) {
      if (prefiereMovimientoReducido()) {
        mapa.setCenter(centro);
        mapa.setZoom(zoom);
      } else {
        mapa.panTo(centro);
        mapa.setZoom(zoom);
      }
    }

    function recolocarMapa(puntos, opciones) {
      opciones = opciones || {};

      if (opciones.vistaNacional) {
        moverVista(CENTRO_ESPANA, ZOOM_INICIAL);
        return;
      }

      // Sin resultados visibles (CP no reconocible, texto sin coincidencias): se deja el mapa
      // donde estaba y el estado vacío ya lo cuenta en la lista, no hay nada que encuadrar.
      if (!puntos || !puntos.length) return;

      if (puntos.length === 1) {
        moverVista({ lat: puntos[0][0], lng: puntos[0][1] }, opciones.zoomUnico || opciones.maxZoom || ZOOM_MAXIMO_RESULTADO);
        return;
      }

      var bounds = new google.maps.LatLngBounds();
      puntos.forEach(function (p) { bounds.extend(new google.maps.LatLng(p[0], p[1])); });
      encuadrarConMaxZoom(bounds, opciones.padding || 48, opciones.maxZoom || ZOOM_MAXIMO_RESULTADO);
    }

    function centrarEnPunto(lat, lng, farmaciaMasCercana) {
      if (farmaciaMasCercana && tieneCoordenadas(farmaciaMasCercana)) {
        var bounds = new google.maps.LatLngBounds();
        bounds.extend(new google.maps.LatLng(lat, lng));
        bounds.extend(new google.maps.LatLng(farmaciaMasCercana.lat, farmaciaMasCercana.lng));
        encuadrarConMaxZoom(bounds, 48, ZOOM_UMBRAL_CLUSTER + 4);
      } else {
        moverVista({ lat: lat, lng: lng }, ZOOM_UMBRAL_CLUSTER + 2);
      }
    }

    // A 375px, con la ficha inferior abierta, su franja ocupa casi todo el ancho del mapa pegada
    // al fondo (left/right: 12px, bottom: 12px) y tapa los controles +/- de Google, que por
    // defecto viven también abajo (Tarea 9 #8). NO se toca ningún z-index interno de Google —
    // ver el comentario largo sobre z-index más arriba en este mismo archivo, junto a
    // .ndk-farmacias__mapa-aviso en el CSS: esto usa `zoomControlOptions.position`, la API
    // pública documentada por Google para mover sus propios controles, no un ajuste de capas.
    // abrirFicha/cerrarFicha (arriba, antes de que el mapa exista) llaman a esto vía
    // mapaAPI.moverControlZoom, que hasta este punto era un no-op.
    function moverControlZoom(arriba) {
      mapa.setOptions({
        zoomControlOptions: {
          position: arriba ? google.maps.ControlPosition.RIGHT_TOP : google.maps.ControlPosition.RIGHT_BOTTOM
        }
      });
    }

    mapaAPI.actualizarCapasVisibles = actualizarCapasVisibles;
    mapaAPI.recolocarMapa = recolocarMapa;
    mapaAPI.centrarEnPunto = centrarEnPunto;
    mapaAPI.moverControlZoom = moverControlZoom;

    window.NdkFarmacias.mapa = mapa;
    window.NdkFarmacias.marcadores = overlaysPinLista.map(function (item) { return item.overlay; });

    // Arranque de la vista. Ojo: "arrancar" NO es lo mismo que "recolocar sobre los
    // resultados". Encuadrar los puntos de las 39 farmacias mete en el mismo bounds Tenerife
    // (28,01 N / -16,67 E) y A Coruña (43,53 N): con los 320/420 px de alto del contenedor,
    // fitBounds baja a zoom 4 y arrastra el centro al Atlántico (36,15 N / -7,29 E), dejando
    // España pegada al borde izquierdo con media Europa y el Magreb en pantalla. La vista de
    // arranque correcta es la nacional (CENTRO_ESPANA / ZOOM_INICIAL), exactamente la misma a
    // la que ya vuelve el buscador cuando se vacía (ver el `vistaNacional: true` de filtrar()).
    // Solo se encuadra otra cosa si el visitante ya había buscado o geolocalizado antes de que
    // Google terminara de cargar; eso es lo que guarda `encuadrePendiente`.
    function aplicarEncuadreInicial() {
      var pendiente = encuadrePendiente;
      encuadrePendiente = null;
      if (pendiente && pendiente.tipo === 'punto') {
        centrarEnPunto(pendiente.lat, pendiente.lng, pendiente.farmacia);
        return;
      }
      if (pendiente && pendiente.tipo === 'recolocar') {
        recolocarMapa(pendiente.puntos, pendiente.opciones);
        return;
      }
      recolocarMapa(null, { vistaNacional: true });
    }

    mapaAPI.actualizarCapasVisibles(ESTADO.filtradas);
    aplicarEncuadreInicial();

    // 'idle' cubre tanto el primer render (para medir la franja de copyright de Google en
    // cuanto exista) como cualquier cambio posterior de tamaño/zoom que pueda alterar su alto.
    mapa.addListener('idle', ajustarOffsetFicha);

    // Red de seguridad para el caso "la página nunca llegó a pintarse". Si la página se abre en
    // una pestaña de fondo (Cmd+click), en un iframe fuera de pantalla o se restaura desde
    // bfcache, el navegador no compone esa pestaña y Google deja su pipeline de render parado:
    // el Map existe y getCenter() responde, pero getProjection() sigue a null, getBounds()
    // devuelve undefined y no se pide ni una baldosa —el cuadro exacto que durante la Tarea 7c
    // se confundió con un mapa roto—. Google reengancha solo al volver la pestaña al frente;
    // esto es solo un empujón por si acaso. Va guardado tras !getProjection() para que nunca
    // pueda deshacer un encuadre del visitante: un mapa sin proyección todavía no se ha
    // dibujado y, por tanto, no se ha podido tocar.
    function empujarSiNuncaRenderizo() {
      if (!mapa.getProjection()) {
        google.maps.event.trigger(mapa, 'resize');
        mapa.setCenter(mapa.getCenter());
      }
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') empujarSiNuncaRenderizo();
    });
    window.addEventListener('pageshow', empujarSiNuncaRenderizo);
  }

  // ---------- filtro principal (buscador de texto/CP) ----------

  // `opcionesMapa` deja que una rama concreta (el fallback nacional) indique explícitamente
  // qué puntos encuadrar en vez de los de la lista completa —mostrar las 39 farmacias de
  // España no ayuda cuando lo relevante es "aquí no hay, la más cercana está a X km"—; el
  // resto de ramas no lo necesitan y dejan que se calculen a partir de la lista de resultados.
  function aplicarResultado(lista, opcionesLista, mostrarCierreForzado, opcionesMapa) {
    ESTADO.filtradas = lista;
    mapaAPI.actualizarCapasVisibles(lista);
    renderizarLista(lista, opcionesLista);
    actualizarCierre(lista, mostrarCierreForzado);
    opcionesMapa = opcionesMapa || {};
    mapaAPI.recolocarMapa(opcionesMapa.puntos || puntosDe(lista), opcionesMapa);
    return lista;
  }

  // Búsqueda por texto (ciudad, provincia, comunidad, nombre) sin distinguir acentos/mayúsculas.
  // Si lo tecleado son solo dígitos y tiene 4 o 5 caracteres se trata como código postal
  // (Alicante/Ceuta empiezan por cero y se suele omitir, de ahí el relleno a 5); cualquier
  // otra cosa (incluyendo dígitos de otra longitud) es una búsqueda de texto normal.
  function filtrar(texto) {
    var raw = String(texto == null ? '' : texto).trim();
    var esCP = /^\d{4,5}$/.test(raw);

    if (esCP) {
      var cp = raw.length === 4 ? ('0' + raw) : raw;
      var busqueda = buscarPorCP(cp);

      if (busqueda.nivel === 'fallback') {
        mostrarAvisoProvincia(busqueda.provincia[2]);
        var masCercana = busqueda.lista[0];
        var lejos = !!(masCercana && typeof masCercana._distanciaKm === 'number' &&
          isFinite(masCercana._distanciaKm) && masCercana._distanciaKm > UMBRAL_LEJOS_KM);
        // No tiene sentido encuadrar las 39 farmacias del país (los puntos por defecto de
        // `aplicarResultado`): lo relevante aquí es la zona buscada, así que se pide
        // explícitamente encuadrar la capital de provincia junto con la farmacia más cercana
        // (o solo la capital si por lo que sea no tuviera coordenadas).
        var puntosFallback = [[busqueda.provincia[0], busqueda.provincia[1]]];
        if (masCercana && tieneCoordenadas(masCercana)) puntosFallback.push([masCercana.lat, masCercana.lng]);
        aplicarResultado(busqueda.lista, { plano: true, mostrarDistancia: true }, lejos, {
          puntos: puntosFallback,
          padding: 48,
          maxZoom: ZOOM_UMBRAL_CLUSTER + 4,
          zoomUnico: ZOOM_UMBRAL_CLUSTER + 2
        });
        return busqueda.lista;
      }

      ocultarAvisoProvincia();
      return aplicarResultado(busqueda.lista);
    }

    ocultarAvisoProvincia();
    var t = normalizarTexto(raw);
    var resultado = !t ? ESTADO.todas.slice() : ESTADO.todas.filter(function (f) {
      return normalizarTexto(f.ciudad).indexOf(t) !== -1 ||
        normalizarTexto(f.cp).indexOf(t) !== -1 ||
        normalizarTexto(f.provincia).indexOf(t) !== -1 ||
        normalizarTexto(f.comunidad).indexOf(t) !== -1 ||
        normalizarTexto(f.nombre).indexOf(t) !== -1;
    });
    // Buscador vaciado (t === '', el usuario ha borrado el texto): resultado = todas las
    // farmacias, pero encuadrar sus puntos daría una vista "ajustada a España" ligeramente
    // distinta cada vez según qué farmacias entren; en vez de eso se vuelve exactamente a la
    // vista nacional inicial.
    return aplicarResultado(resultado, undefined, undefined, t ? undefined : { vistaNacional: true });
  }

  function ordenarPorDistancia(lat, lng) {
    if (typeof lat !== 'number' || typeof lng !== 'number') return ESTADO.filtradas;
    var base = ESTADO.filtradas.length ? ESTADO.filtradas : ESTADO.todas;
    base.forEach(function (f) {
      f._distanciaKm = tieneCoordenadas(f) ? distanciaHaversine(lat, lng, f.lat, f.lng) : Infinity;
    });
    var ordenado = base.slice().sort(function (a, b) { return a._distanciaKm - b._distanciaKm; });
    ESTADO.filtradas = ordenado;
    renderizarLista(ordenado, { plano: true, mostrarDistancia: true });
    actualizarCierre(ordenado);
    return ordenado;
  }

  // ---------- buscador de texto/CP ----------

  var elBuscadorInput = document.getElementById('ndk-farmacias-buscador-input');
  if (elBuscadorInput) {
    elBuscadorInput.addEventListener('input', function () {
      filtrar(elBuscadorInput.value);
    });
  }

  // ---------- geolocalización ----------

  var elGeoBoton = document.getElementById('ndk-farmacias-geo-boton');
  var elGeoBotonTexto = document.getElementById('ndk-farmacias-geo-boton-texto');
  var elGeoEstado = document.getElementById('ndk-farmacias-geo-estado');
  var TEXTO_GEO_BOTON_DEFECTO = elGeoBotonTexto ? elGeoBotonTexto.textContent : '';

  function mostrarEstadoGeo(mensaje) {
    if (!elGeoEstado) return;
    elGeoEstado.textContent = mensaje || '';
  }

  function establecerGeoCargando(cargando) {
    if (elGeoBoton) elGeoBoton.disabled = cargando;
    if (elGeoBotonTexto) elGeoBotonTexto.textContent = cargando ? 'Buscando tu ubicación…' : TEXTO_GEO_BOTON_DEFECTO;
  }

  function usarMiUbicacion() {
    if (!navigator.geolocation) {
      mostrarEstadoGeo('No hemos podido obtener tu ubicación.');
      return;
    }
    mostrarEstadoGeo('');
    establecerGeoCargando(true);
    navigator.geolocation.getCurrentPosition(
      function (posicion) {
        establecerGeoCargando(false);
        var lat = posicion.coords.latitude;
        var lng = posicion.coords.longitude;
        var ordenado = ordenarPorDistancia(lat, lng);
        mapaAPI.centrarEnPunto(lat, lng, ordenado[0]);
      },
      function () {
        establecerGeoCargando(false);
        mostrarEstadoGeo('No hemos podido obtener tu ubicación.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  if (elGeoBoton) {
    elGeoBoton.addEventListener('click', usarMiUbicacion);
  }

  // ---------- inicialización ----------
  // Se hace de inmediato, sin esperar a Google Maps (que puede tardar en cargar, o no cargar
  // nunca si no hay clave configurada): la lista, el buscador y la geolocalización no dependen
  // del mapa. inicializarMapa (arriba) rellena mapaAPI y window.NdkFarmacias.mapa cuando —y
  // si— Google termina de cargar (ver la llamada a google.maps.importLibrary más abajo).

  renderizarLista(TODAS);
  actualizarCierre(TODAS);
  ajustarOffsetFicha();
  window.addEventListener('resize', ajustarOffsetFicha);

  // Diagnóstico desde consola. Existe por una razón concreta: durante la Tarea 7c el mapa se
  // dio por roto midiéndolo desde una pestaña que nunca pintaba un frame. En una pestaña que no
  // se compone, Google construye el Map pero no arranca su render, y getProjection() /
  // getBounds() devuelven EXACTAMENTE lo mismo que devolvería un mapa roto de verdad. Por eso
  // el primer campo es `visibilityState`: si no es 'visible', ningún otro campo significa nada
  // y la medición hay que repetirla con la pestaña delante.
  function diagnostico() {
    var mapa = window.NdkFarmacias.mapa;
    var bounds = mapa && mapa.getBounds();
    var centro = mapa && mapa.getCenter();
    return {
      visibilityState: document.visibilityState,
      hayMapa: !!mapa,
      proyeccion: !!(mapa && mapa.getProjection()),
      bounds: bounds ? bounds.toUrlValue() : null,
      zoom: mapa ? mapa.getZoom() : null,
      centro: centro ? centro.toUrlValue() : null,
      baldosas: elMapaGoogle ? elMapaGoogle.querySelectorAll('img[src*="maps.googleapis.com"]').length : 0,
      clusters: document.querySelectorAll('.ndk-farmacias__cluster').length,
      marcadores: window.NdkFarmacias.marcadores.length,
      // Resultado de fusionar el Excel con los bloques del editor. Va el ÚLTIMO justamente
      // porque `visibilityState` tiene que seguir siendo el primer campo (ver arriba).
      fusion: { resumen: FUSION.resumen, avisos: FUSION.avisos }
    };
  }

  // Los textos de ayuda de los bloques "Corregir" y "Ocultar" mandan a copiar el identificador
  // del panel del editor; esto es lo mismo desde la consola, para quien no esté en el editor.
  function ids() {
    return TODAS.map(function (f) { return { id: f.id, nombre: f.nombre }; });
  }

  window.NdkFarmacias = {
    mapa: null,
    marcadores: [],
    filtrar: filtrar,
    ordenarPorDistancia: ordenarPorDistancia,
    diagnostico: diagnostico,
    ids: ids
  };

  // ---------- panel de ayuda del editor de temas ----------
  // El contenedor solo lo imprime el Liquid bajo request.design_mode, así que este bloque no
  // hace nada en la página pública: si el elemento no está, se sale y ya.

  function pintarPanelEditor() {
    var el = document.getElementById('ndk-farmacias-editor-avisos');
    if (!el) return;

    var r = FUSION.resumen;
    var html = '<p class="ndk-farmacias__editor-resumen">' +
      r.base + ' del Excel · ' + r.corregidas + ' corregidas · ' + r.nuevas + ' añadidas · ' +
      r.ocultas + ' ocultas · ' + r.descartados + ' bloques ignorados · ' +
      TODAS.length + ' en la página</p>';

    if (FUSION.avisos.length) {
      html += '<ul class="ndk-farmacias__editor-lista">';
      FUSION.avisos.forEach(function (a) {
        var clase = a.severidad === 'error' ? 'error' : 'aviso';
        html += '<li class="ndk-farmacias__editor-item ndk-farmacias__editor-item--' + clase + '">' +
          (a.posicion ? '<strong>Bloque ' + a.posicion + ':</strong> ' : '') +
          escapeHtml(a.texto) + '</li>';
      });
      html += '</ul>';
    }

    var ordenados = TODAS.slice().sort(function (a, b) {
      return String(a.id).localeCompare(String(b.id), 'es');
    });
    html += '<details class="ndk-farmacias__editor-ids"><summary>Identificadores de las ' +
      ordenados.length + ' farmacias (cópialos para los bloques de corregir y ocultar)</summary><ul>';
    ordenados.forEach(function (f) {
      html += '<li><code>' + escapeHtml(f.id) + '</code> — ' + escapeHtml(f.nombre) + '</li>';
    });
    html += '</ul></details>';

    el.innerHTML = html;
    el.hidden = false;
  }

  pintarPanelEditor();

  // Recarga de la vista previa al editar. Cualquier cambio de ajuste o de bloque hace que
  // Shopify vuelva a renderizar la sección entera y SUSTITUYA su DOM; este IIFE ya se ejecutó y
  // guarda referencias a los nodos viejos, así que sin recargar el fundador editaría un bloque
  // y no vería cambiar nada — peor: la lista se quedaría clavada en "Cargando farmacias…" y el
  // mapa desaparecería, que es exactamente la experiencia que hace desconfiar de la
  // herramienta. La vista previa del editor es un iframe aparte del formulario de ajustes, así
  // que recargarla NO le quita el foco del campo que esté escribiendo.
  // El guardado en sessionStorage es un cortafuegos: si algún día el editor disparase este
  // evento nada más cargar, sin él la vista previa entraría en un bucle de recargas.
  if (window.Shopify && window.Shopify.designMode) {
    var temporizadorRecarga = null;
    document.addEventListener('shopify:section:load', function (e) {
      if (!e.target || !e.target.querySelector || !e.target.querySelector('#ndk-farmacias-mapa')) return;
      if (temporizadorRecarga) clearTimeout(temporizadorRecarga);
      temporizadorRecarga = setTimeout(function () {
        var ahora = Date.now();
        var ultima = 0;
        try { ultima = Number(window.sessionStorage.getItem('ndkf-recarga-editor') || 0); } catch (err) { /* sessionStorage bloqueado */ }
        if (ahora - ultima < 1500) return;
        try { window.sessionStorage.setItem('ndkf-recarga-editor', String(ahora)); } catch (err) { /* idem */ }
        window.location.reload();
      }, 400);
    });
  }

  // Arranque del mapa (Tarea 7b): se llama a google.maps.importLibrary('maps') directamente en
  // vez de depender de un callback= global que Google invocaría por su cuenta. Con
  // loading=async, esa combinación (callback= + loading=async) no garantiza que el callback
  // llegue a dispararse durante la carga normal de la página — Google la desaconseja
  // explícitamente a favor de importLibrary. Aquí no importa en qué orden terminen de cargar el
  // bootstrap loader de Google (script inline insertado por la sección solo cuando hay clave,
  // ver sections/ndk-farmacias-section.liquid) y este archivo: si google.maps.importLibrary
  // existe la llamamos, y su promesa resuelve cuando la librería 'maps' esté lista, sin
  // carrera posible. Sin clave configurada la sección no inserta el bootstrap loader,
  // google.maps no existe y este bloque no hace nada: el resto de la página funciona igual.
  if (window.google && window.google.maps && typeof google.maps.importLibrary === 'function') {
    google.maps.importLibrary('maps').then(inicializarMapa).catch(function (err) {
      // eslint-disable-next-line no-console
      console.error('NDK Farmacias: no se pudo cargar Google Maps.', err);
    });
  }
})();
